/**
 * @fileoverview Tool System Interface Definitions
 * 
 * This file defines the core interfaces and types for the tool system, which provides
 * a pluggable architecture for canvas operations. Tools are self-contained units that
 * can perform operations like selection, creation, transformation, and styling.
 * 
 * Key Concepts:
 * - Tools are isolated, reusable components
 * - Each tool has a manifest describing its capabilities
 * - Tools can provide UI components for different locations
 * - Tools operate within a context (selection, frame, etc.)
 * - Tools generate commands for undo/redo support
 * 
 * Architecture:
 * - Tool Interface (ITool) - Core tool contract
 * - Tool Manifest (IToolManifest) - Tool metadata and capabilities
 * - Tool Context (IToolContext) - Current application state
 * - Tool Registry - Manages tool lifecycle and activation
 * 
 * @author Claude Code
 * @version 1.0.0
 */

import { BaseShape } from '../../shapes/BaseShape';
import { BaseConnector } from '../../connectors/BaseConnector';
import Konva from 'konva';
import { ICommand } from '../../commands/ICommand';
import { IFrame } from '../../frames/IFrame';
import { IEventBus } from '../../events/IEventBus';
import type * as Y from 'yjs';

// Re-export ICommand for convenience
export type { ICommand } from '../../commands/ICommand';
import React from 'react';

/** Supported diagram types in the application */
export type DiagramType = 'genogram' | 'flowchart' | 'mindmap' | 'network' | 'org' | 'database' | 'uml' | 'planning' | 'software-design' | 'generic';

/** Categories for organizing tools */
export type ToolCategory = 'edit' | 'create' | 'transform' | 'style' | 'analyze' | 'layout';

// Context-type driven emergent UI support
export type ContextType = 'none' | 'text' | 'person' | 'genericShape' | 'connector' | 'frame' | 'sticky-note' | 'image' | 'pdf' | 'mixed';

/**
 * Who owns presentation (layout + style) for the current diagram.
 * 'system' = layout engine + lenses own it (managed charts); 'user' = the
 * user authors placement and style directly (manual drawing, freeform
 * domains). Domains that never publish a value are treated as 'user'.
 */
export type PresentationOwnership = 'user' | 'system';

export interface SelectionSummary {
  contextType: ContextType;
  typesSet: Set<ContextType>;
  countsByType: Record<ContextType, number>;
  diagramType: DiagramType;
  flags: {
    hasText?: boolean;
    hasConnectors?: boolean;
  };
  version: number;
}

export interface SelectionStylesAggregate {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  textColor?: string;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  dash?: number[];
  dashEnabled?: boolean;
  opacity?: number;
  textDecoration?: string;
  // Connector-specific
  edgeColor?: string;
  edgeDashed?: boolean;
  edgeEndArrow?: 'none' | 'triangle' | 'circle' | 'diamond' | 'bar';
  edgeRoutingType?: 'straight' | 'orthogonal' | 'orthogonalRounded' | 'bezier';
  edgeLabel?: string;
  edgeLabelOffset?: number;
  isMixed: Partial<Record<keyof SelectionStylesAggregate, boolean>>;
}

export interface SelectionDataAggregate {
  // Person (genogram) common fields
  name?: string;
  sex?: 'male' | 'female' | 'unknown';
  deceased?: boolean;
  birthDate?: string;
  deathDate?: string;
  location?: string;
  culturalHeritageTagsCount?: number;
  medicalConditionTagsCount?: number;
  // Person (org chart) common fields
  departureDate?: string;
  isMixed: Partial<Record<keyof SelectionDataAggregate, boolean>>;
}

/**
 * Represents a keyboard shortcut for a tool.
 */
export interface IKeyboardShortcut {
  /** The key to press (e.g., 'c', 'Delete', 'Escape') */
  key: string;
  
  /** Modifier keys that must be held (e.g., ['ctrl', 'shift']) */
  modifiers?: ('ctrl' | 'cmd' | 'shift' | 'alt')[];
  
  /** Human-readable description of what the shortcut does */
  description: string;
}

/**
 * Represents an item in a context menu provided by a tool.
 */
export interface IContextMenuItem {
  /** Unique identifier for this menu item */
  id: string;
  
  /** Display text for the menu item */
  label: string;
  
  /** Optional icon to display (emoji or icon name) */
  icon?: string;
  
  /** Function to call when the item is clicked */
  action: () => void;
  
  /** Whether the item is currently enabled/clickable */
  enabled?: boolean;
  
  /** Whether this item is a separator line */
  separator?: boolean;
  
  /** Sub-menu items if this is a submenu */
  submenu?: IContextMenuItem[];
}

export interface IToolManifest {
  id: string;
  name: string;
  icon: string;
  category: ToolCategory;
  description: string;
  version: string;
  
  // Context requirements
  supportedDiagramTypes?: DiagramType[];
  supportedShapeTypes?: string[];
  requiresSelection?: boolean;
  requiresFrame?: boolean;
  minSelection?: number;
  maxSelection?: number;
  
  // UI mounting points
  ui: {
    /** Contextual toolbar, trailing (style) cluster — the legacy location. */
    toolbar?: boolean;
    contextMenu?: boolean;
    sidebar?: boolean;
    propertyPanel?: boolean;
    floatingPanel?: boolean;
    statusBar?: boolean;
    /** Contextual toolbar, leading cluster: model verbs on the selection. */
    selectionAction?: boolean;
    /** Bottom toolbar, creation cluster. */
    canvasCreate?: boolean;
    /** Bottom toolbar, mode cluster. */
    canvasMode?: boolean;
  };

  // Applicability for emergent UI (optional; falls back to canExecute)
  applicability?: {
    contextTypes?: ContextType[];
    requires?: Partial<SelectionSummary['flags']>;
    diagramTypes?: DiagramType[];
    frameTags?: string[];
    varianceId?: string;
    /** Omit = mounts under both ownership modes. */
    presentationModes?: PresentationOwnership[];
  };

  // Chrome mount presentation hints
  chrome?: {
    /** Intra-mount ordering, ascending. Default 100. */
    order?: number;
  };
  
  // Capabilities
  capabilities: {
    modifiesShapes: boolean;
    createsShapes: boolean;
    deletesShapes: boolean;
    modifiesConnectors: boolean;
    createsConnectors: boolean;
    deletesConnectors: boolean;
    requiresUserInput: boolean;
    supportsMultiSelect: boolean;
    supportsBatchOperations: boolean;
  };
  
  // Plan-based capability requirements
  requiredCapabilities?: string[];
  
  // Keyboard shortcuts
  shortcuts?: IKeyboardShortcut[];
  
  // Tool dependencies (other tools that must be loaded)
  dependencies?: string[];
  
  // Configuration schema (for tool settings)
  configSchema?: Record<string, any>;
}

export interface IToolContext {
  selection: Set<string>;
  shapes: Map<string, BaseShape<any>>;
  connectors: Map<string, BaseConnector<any>>;
  frame: IFrame | null;
  /** Explicit frame selection, distinct from the active frame's editing scope. */
  selectedFrameId?: string | null;
  diagramType: DiagramType;
  stage: Konva.Stage;
  layer: Konva.Layer;
  eventBus: IEventBus;
  doc?: Y.Doc; // Yjs document for persistence
  selectionSummary?: SelectionSummary;
  selectionStyles?: SelectionStylesAggregate;
  selectionData?: SelectionDataAggregate;

  /**
   * Who owns presentation (layout + style) right now. Supplied by the active
   * domain canvas plugin; absent means 'user' so freeform domains keep their
   * style chrome without manifest edits.
   */
  presentationOwnership?: PresentationOwnership;

  /**
   * True when the canvas is a projection of live records with no active
   * draft to receive structural edits (e.g. a live people/position chart
   * outside "Make changes"). Structural verbs gate on this: a command that
   * writes raw nodes into a projection-locked canvas saves but never renders.
   */
  projectionLocked?: boolean;

  /**
   * True for an Atlas saved custom view (source-backed, filters-only,
   * functionally read-only — scenario/succession authoring never commits
   * for it). Distinct from `projectionLocked`, which is also true for the
   * canonical Full Org Chart, where those workflows ARE meaningful.
   */
  isSavedAtlasView?: boolean;

  // User permissions
  canWrite: boolean;
  
  // Plan-based entitlements
  entitlements?: {
    planType: string;
    capabilities: {
      capabilities: string[];
      maxShapesPerDiagram: number;
      export: { allowedFormats: string[] };
      sharing: { mode: string; autoEnrollOnLinkAccess: boolean; allowPerEmailInvites: boolean };
    };
  };
  
  // Helper methods
  getSelectedShapes(): BaseShape<any>[];
  getSelectedConnectors(): BaseConnector<any>[];
  getShapesInFrame(frameId: string): BaseShape<any>[];

  // Optional command factory for enhanced features (e.g., comment restoration)
  createDeleteNodeCommand?: (nodeIds: string[]) => ICommand;
}

export interface ITool {
  manifest: IToolManifest;
  
  // Lifecycle
  initialize?(config?: any): Promise<void>;
  onActivate(context: IToolContext): void;
  onDeactivate(): void;
  dispose?(): void;
  
  // Context changes
  onSelectionChange(_selection: Set<string>): void;
  onFrameChange(_frame: IFrame | null): void;
  onDiagramTypeChange?(type: DiagramType): void;
  
  // Check if tool can execute in current context
  canExecute(context: IToolContext): boolean;

  /**
   * Optional property-panel visibility check. Use this when a tool's panel
   * supports read-only inspection even though its commands require write
   * access. Mutation authorization must remain in canExecute/commands.
   */
  canRenderPropertyPanel?(context: IToolContext): boolean;

  /**
   * Optional toggle-state probe for chrome mounts: when implemented, the
   * default chrome button renders pressed (aria-pressed) while this returns
   * true, and executing the tool is expected to flip the state.
   */
  getActiveState?(context: IToolContext): boolean;
  
  // Execute tool operation
  execute(params?: any): ICommand | ICommand[] | null | Promise<ICommand | ICommand[] | null>;
  
  // UI Components (all optional)
  getToolbarComponent?(): React.ComponentType<IToolComponentProps>;
  getSidebarComponent?(): React.ComponentType<IToolComponentProps>;
  getContextMenuItems?(context: IToolContext): IContextMenuItem[];
  getPropertyPanelComponent?(): React.ComponentType<IToolComponentProps>;
  getFloatingPanelComponent?(): React.ComponentType<IToolComponentProps>;
  getStatusBarComponent?(): React.ComponentType<IToolComponentProps>;
  
  // Configuration
  getConfig?(): any;
  setConfig?(config: any): void;
  validateConfig?(config: any): boolean;
  
  // Events
  on?(event: string, handler: (...args: any[]) => void): void;
  off?(event: string, handler: (...args: any[]) => void): void;
  emit?(event: string, ...args: any[]): void;
}

export interface IToolComponentProps {
  tool: ITool;
  context: IToolContext;
  onExecute: (params?: any) => void;
  config?: any;
}

// Base implementation
export abstract class BaseTool implements ITool {
  abstract manifest: IToolManifest;
  protected context: IToolContext | null = null;
  protected config: any = {};
  
  async initialize(config?: any): Promise<void> {
    if (config) {
      this.config = config;
    }
  }
  
  onActivate(context: IToolContext): void {
    this.context = context;
  }
  
  onDeactivate(): void {
    this.context = null;
  }
  
  onSelectionChange(_selection: Set<string>): void {
    // Override in subclasses if needed
  }
  
  onFrameChange(_frame: IFrame | null): void {
    // Override in subclasses if needed
  }
  
  canExecute(context: IToolContext): boolean {
    // Check write permission for tools that modify the diagram
    const hasWriteCapability = this.manifest.capabilities?.createsShapes ||
      this.manifest.capabilities?.modifiesShapes ||
      this.manifest.capabilities?.deletesShapes ||
      this.manifest.capabilities?.createsConnectors ||
      this.manifest.capabilities?.modifiesConnectors ||
      this.manifest.capabilities?.deletesConnectors;
    
    if (hasWriteCapability && !context.canWrite) {
      return false;
    }
    if (hasWriteCapability && context.isSavedAtlasView) {
      return false;
    }
    
    // Check basic requirements
    if (this.manifest.requiresSelection && context.selection.size === 0) {
      return false;
    }
    
    if (this.manifest.minSelection && context.selection.size < this.manifest.minSelection) {
      return false;
    }
    
    if (this.manifest.maxSelection && context.selection.size > this.manifest.maxSelection) {
      return false;
    }
    
    if (this.manifest.requiresFrame && !context.frame) {
      return false;
    }
    
    if (this.manifest.supportedDiagramTypes && 
        !this.manifest.supportedDiagramTypes.includes(context.diagramType)) {
      return false;
    }
    
    return true;
  }
  
  abstract execute(params?: any): ICommand | ICommand[] | null | Promise<ICommand | ICommand[] | null>;
  
  getConfig(): any {
    return this.config;
  }
  
  setConfig(config: any): void {
    if (this.validateConfig(config)) {
      this.config = config;
    }
  }
  
  validateConfig(_config: any): boolean {
    // Override in subclasses for custom validation
    return true;
  }
  
  dispose(): void {
    this.context = null;
    this.config = {};
  }
}
